import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import DiaryCalendar from '../components/Calendar';
import api from '../utils/api';
import { Resident } from '../types';
import './Diary.css';

const Diary: React.FC = () => {
  const { residentId } = useParams<{ residentId: string }>();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [resident, setResident] = useState<Resident | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchResident = useCallback(async () => {
    try {
      const response = await api.get(`/residents/${residentId}`);
      if (response.data.success) {
        setResident(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching resident:', error);
    } finally {
      setLoading(false);
    }
  }, [residentId]);

  useEffect(() => {
    if (residentId) {
      fetchResident();
    } else {
      setLoading(false);
    }
  }, [residentId, fetchResident]);

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
  };

  if (loading) {
    return (
      <div className="diary-loading">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  // Show overview calendar if no resident ID selected
  if (!residentId) {
    return (
      <div className="diary-container">
        <div className="diary-header">
          <div className="header-content">
            <div className="resident-info">
              <h1>All Appointments Overview</h1>
              <div className="resident-details">
                <span className="age">System-wide calendar showing all residents' appointments</span>
              </div>
            </div>
          </div>
        </div>

        <div className="diary-content">
          <div className="calendar-section-full">
            <DiaryCalendar
              residentId={undefined}
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
            />
          </div>
        </div>
      </div>
    );
  }

  if (!resident && !loading) {
    return (
      <div className="diary-container">
        <div className="error-message">
          <h2>Resident not found</h2>
          <p>The selected resident could not be found.</p>
        </div>
      </div>
    );
  }

  // Type guard: if we reach here, resident must exist
  if (!resident) {
    return null; // This should never happen due to above checks
  }

  return (
    <div className="diary-container">
      <div className="diary-header">
        <div className="header-content">
          <div className="resident-info">
            <h1>Diary for {resident.name}</h1>
            <div className="resident-details">
              <span className="age">Age: {resident.age}</span>
              <span className="diagnosis">{resident.diagnosis}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="diary-content">
        <div className="calendar-section-full">
          <DiaryCalendar
            residentId={residentId}
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
          />
        </div>
      </div>

    </div>
  );
};

export default Diary;